import React, { useRef, useState, useEffect } from 'react';
import Icon from './icons';
import { useTranslation } from 'react-i18next';

interface ImageSourceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCameraSelect: () => void;
  onFileSelect: (file: File) => void;
}

const ImageSourceModal: React.FC<ImageSourceModalProps> = ({
  isOpen,
  onClose,
  onCameraSelect,
  onFileSelect,
}) => {
  const { t } = useTranslation();
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const uploadInputRef = useRef<HTMLInputElement>(null);
  const [isMobile, setIsMobile] = useState(false);
  const [isConnectingHIS, setIsConnectingHIS] = useState(false);

  // Detect if the device is mobile
  useEffect(() => {
    const checkMobile = () => {
      const userAgent = navigator.userAgent || navigator.vendor;
      const isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
      setIsMobile(isMobileDevice);
      console.log('Device detection:', isMobileDevice ? 'Mobile' : 'Desktop');
    };

    if (isOpen) {
      checkMobile();
    }
  }, [isOpen]);

  const openFileDialog = () => {
    if (uploadInputRef.current) {
      uploadInputRef.current.click();
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      const file = files[0];
      onFileSelect(file);
    }
  };

  const handleCameraSelect = () => {
    console.log('Camera button clicked');
    if (isMobile) {
      // On mobile, use the file input with capture attribute
      if (cameraInputRef.current) {
        cameraInputRef.current.click();
      }
    } else {
      // On desktop, use the camera preview component
      onCameraSelect();
    }
  };

  const handleHISConnect = () => {
    setIsConnectingHIS(true);
    
    // Simulate connection attempt for 3 seconds
    setTimeout(() => {
      setIsConnectingHIS(false);
      // In a real application, you would handle the HIS connection result here
    }, 30000);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-gray-900 bg-opacity-75 flex items-center justify-center">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="p-4 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-medium text-gray-900">{t('imageSourceModal.title')}</h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500"
            >
              <span className="sr-only">{t('imageSourceModal.close')}</span>
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
        
        <div className="p-6">
          <div className="grid grid-cols-1 gap-4">
            <button
              onClick={handleCameraSelect}
              className="flex items-center p-4 border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <div className="flex-shrink-0 mr-4">
                <Icon iconName="OutlineCamera" size={32} color="#4f46e5" />
              </div>
              <div className="text-left">
                <div className="text-lg font-medium text-gray-900">
                  {t('imageSourceModal.takePicture.title')}
                </div>
                <div className="text-sm text-gray-500">
                  {t('imageSourceModal.takePicture.description')}
                </div>
              </div>
            </button>
            
            <button
              onClick={openFileDialog}
              className="flex items-center p-4 border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <div className="flex-shrink-0 mr-4">
                <Icon iconName="OutlineCloudUpload" size={32} color="#4f46e5" />
              </div>
              <div className="text-left">
                <div className="text-lg font-medium text-gray-900">{t('imageSourceModal.uploadImage.title')}</div>
                <div className="text-sm text-gray-500">{t('imageSourceModal.uploadImage.description')}</div>
              </div>
            </button>
            
            <button
              onClick={handleHISConnect}
              disabled={isConnectingHIS}
              className="flex items-center p-4 border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <div className="flex-shrink-0 mr-4">
                {isConnectingHIS ? (
                  <div className="animate-spin h-8 w-8 text-indigo-600">
                    <Icon iconName="Sync" size={32} color="#4f46e5" />
                  </div>
                ) : (
                  <Icon iconName="Storage" size={32} color="#4f46e5" />
                )}
              </div>
              <div className="text-left">
                <div className="text-lg font-medium text-gray-900">{t('imageSourceModal.importFromHIS.title')}</div>
                <div className="text-sm text-gray-500">
                  {isConnectingHIS 
                    ? t('imageSourceModal.importFromHIS.connecting')
                    : t('imageSourceModal.importFromHIS.description')}
                </div>
              </div>
            </button>
            
            <input
              type="file"
              ref={cameraInputRef}
              onChange={handleFileSelect}
              accept="image/*"
              className="hidden"
              capture={isMobile ? "environment" : undefined}
            />
            <input
              type="file"
              ref={uploadInputRef}
              onChange={handleFileSelect}
              accept="image/*"
              className="hidden"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImageSourceModal; 